import { useState, useEffect, useRef } from 'react';
import { B } from '../../constants/brand';
import { api } from '../../api/client';
import { useApi } from '../../hooks/useApi';
import { fmt } from '../../utils/format';
import { Card, Spinner, ErrorMsg, EmptyState, Icon, Btn, useToast } from '../ui';

// ============================================================
// Capital Deployment Verification
//
// A GL-vs-deal-book reconciliation: every Sage location carrying a balance
// (SUM(debits) − SUM(credits) ≠ 0) that is NOT tagged to a deal or deal detail.
// Each row is capital that has moved in Sage but that no deal claims — so it is
// invisible in every deal roll-up and capital report until someone tags the
// location on a deal detail (Deals → the detail → Sage location).
//
// Listed in THREE SECTIONS (see SECTIONS below): locations not tagged to a
// deal, then entities not tagged to a deal, then codes on no Sage master at
// all. The server decides each row's kind at compute time (migration 196);
// this component only groups.
//
// The numbers are a PRECOMPUTED SNAPSHOT, not a live query: the check aggregates
// the whole GL, which is far too slow for a dashboard request (it timed out).
// A job refreshes it after every Sage GL import and daily; GET
// /api/dashboard/capital-deployment just reads it, so this widget is always
// fast. Hence the "as of" stamp.
//
// IT REFRESHES ITSELF, BUT ONLY WHEN IT KNOWS IT IS WRONG — never simply
// because the dashboard loaded. Recomputing on mount would run that whole-GL
// aggregation on every page view, per admin, per tab, which is the one cost the
// snapshot exists to avoid. Instead the GET carries `resolvedSinceSnapshot`:
// how many rows on screen have since been tagged to a deal, answered by a cheap
// probe over the tagging tables that never touches the GL. Non-zero (or a
// snapshot past the staleness window) fires ONE background recompute and
// re-reads; everything else leaves the read at two indexed lookups.
//
// The old list stays on screen for the whole recompute — a widget that blanks
// itself for a minute because it is quietly correcting a stale row is worse
// than one showing numbers a minute old, and the stamp says how old they are.
// Only the re-read afterwards (two indexed lookups) flickers, as it always has.
//
// A ROW CAN BE IGNORED (migration 203). Some codes are never going to be
// tagged and never going to be fixed in Sage either — a legacy code, corporate
// overhead, a balance somebody has already looked at — and left on the list
// they sit beside the rows that matter until nobody reads either. Ignore takes
// the code off the default list AND out of the headline totals (the server
// subtracts it; this component computes nothing), records who and why, and the
// "Show ignored" checkbox in the header brings every ignored row back, marked,
// with a Restore. The ignore is a row per CODE on its own table, applied when
// the snapshot is read, so it survives every refresh. The checkbox's state is
// remembered per browser (localStorage, a per-viewer convenience).
//
// Restricted endpoint; the widget is filtered from the catalog for everyone
// but the two named viewers, so nobody else renders it.
// ============================================================

// Rows shown before "Show all" — the list is balance-ranked, so the top few are
// the material ones and a long tail of small strays stays collapsed.
const PREVIEW_ROWS = 6;

// The refresh recomputes the full aggregation, so it needs far longer than the
// client's 10s default (same convention as utils/exportDownload.js).
const REFRESH_TIMEOUT_MS = 240000;

// The "Show ignored" checkbox is remembered per browser. Every accessor is
// guarded: storage can be absent or throw (private window, cleared site data)
// and the widget must render either way — this is a convenience, not state.
const SHOW_IGNORED_KEY = 'capitalDeployment.showIgnored';
const readShowIgnored = () => { try { return localStorage.getItem(SHOW_IGNORED_KEY) === '1'; } catch { return false; } };
const writeShowIgnored = (v) => { try { localStorage.setItem(SHOW_IGNORED_KEY, v ? '1' : '0'); } catch { /* per-viewer convenience only */ } };

const Metric = ({ label, value, color = B.text, title }) => (
  <div style={{ minWidth: 120 }} title={title}>
    <div style={{ fontSize: 11, fontWeight: 600, color: B.textMd, textTransform: 'uppercase', letterSpacing: 0.5 }}>
      {label}
    </div>
    <div style={{ fontSize: 18, fontWeight: 700, color, marginTop: 2 }}>{value}</div>
  </div>
);

// Rows are deliberately NOT click-through: the whole point of a row is that the
// location has no deal to navigate to. Clearing it means finding the right deal
// detail and tagging the location there (the footer hint says so).
// THE THREE SECTIONS, IN ORDER — mirrored from KINDS in api/src/capitalDeployment.js
// (keep the two in lockstep; a test pins it). Each kind is cleared from a
// DIFFERENT screen, which is the whole reason the list is broken up: a
// location is tagged on a deal detail, an entity is attached to the deal
// ("+ Sage entity" on the deal header — a detail can only point at a location,
// so a note receivable modelled in Sage as an entity clears no other way), and
// a code on neither master can only be fixed in Sage. Under one heading the
// second and third were indistinguishable, both wearing the red chip. The API
// decides the kind, and the ENTITY master wins when a code is on both — a
// Locations import writes every entity into the location master too (in Sage
// an entity is a top-level location), and the first cut let the location win,
// which filed every entity in the book under "tag it on a deal detail".
const SECTIONS = [
  {
    kind: 'location',
    title: 'Locations not tagged to a deal',
    hint:  'Tag the location on a deal detail (Deals → the detail → Sage location).',
    column: 'Sage location',
  },
  {
    kind: 'entity',
    title: 'Entities not tagged to a deal',
    hint:  'Attach the entity to its deal ("+ Sage entity" on the deal header). A deal detail cannot point at an entity.',
    column: 'Sage entity',
  },
  {
    kind: 'unknown',
    title: 'Not in the Sage master',
    hint:  'These codes post in the GL but are on neither the location nor the entity master — import the Sage export, or correct the code in Sage. Nothing in Harvus can claim them.',
    column: 'GL code',
  },
];

export const CapitalDeploymentWidget = () => {
  const [showIgnored, setShowIgnored] = useState(readShowIgnored);
  // The server applies the ignore before ranking and before the totals, so the
  // checkbox is a request parameter rather than a client-side filter — hiding
  // rows here would leave the headline still counting them.
  const { data, loading, error, reload } = useApi(
    () => api.get(`dashboard/capital-deployment${showIgnored ? '?includeIgnored=1' : ''}`), [showIgnored]);
  const [showAll, setShowAll] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefreshing, setAutoRefreshing] = useState(false);
  // Which row's "Ignore" is open for a reason, and the reason being typed.
  const [ignoring, setIgnoring] = useState(null);
  const [ignoreReason, setIgnoreReason] = useState('');
  const [ignoreBusy, setIgnoreBusy] = useState(null);
  const autoFired = useRef(false);
  const { toast } = useToast();

  const toggleShowIgnored = (v) => { writeShowIgnored(v); setShowIgnored(v); };

  // Ignore a code, with an optional reason. The list re-reads afterwards: the
  // server is what decides both the list and the totals, and the row leaving
  // the headline is the half that matters.
  const ignoreCode = async (sageCode) => {
    setIgnoreBusy(sageCode);
    try {
      const res = await api.post('dashboard/capital-deployment/ignores', { sageCode, reason: ignoreReason.trim() || null });
      setIgnoring(null); setIgnoreReason('');
      toast.success(res?.created === false ? `${sageCode} was already ignored` : `Ignored ${sageCode}`);
      reload();
    } catch (e) {
      toast.error(e.message || 'Could not ignore this code');
    } finally { setIgnoreBusy(null); }
  };

  // Restore an ignored code to the list (a soft delete of the ignore row).
  const restoreCode = async (sageCode) => {
    setIgnoreBusy(sageCode);
    try {
      await api.del(`dashboard/capital-deployment/ignores?sageCode=${encodeURIComponent(sageCode)}`);
      toast.success(`${sageCode} is back on the list`);
      reload();
    } catch (e) {
      toast.error(e.message || 'Could not restore this code');
    } finally { setIgnoreBusy(null); }
  };

  // Recompute server-side, then re-read the snapshot. Long timeout: this runs
  // the full GL aggregation the snapshot exists to avoid doing per-request.
  const recompute = async ({ auto = false } = {}) => {
    const setBusy = auto ? setAutoRefreshing : setRefreshing;
    setBusy(true);
    try {
      const res = await api.post('dashboard/capital-deployment/refresh', { auto }, { timeout: REFRESH_TIMEOUT_MS });
      reload();
      // A refresh that stood down behind one already running has NOT produced
      // these numbers, and reload() will have re-read the same snapshot — so
      // saying "refreshed" would be a plain untruth about the figures on screen.
      if (!auto) {
        if (res?.skipped === 'in-progress') toast.info('A refresh is already running — this will update shortly');
        else toast.success('Capital deployment check refreshed');
      }
    } catch (e) {
      // NOBODY ASKED FOR THE AUTOMATIC ONE, so it fails quietly: the snapshot on
      // screen is still a valid answer, just an older one, and the "as of" stamp
      // already says as much. A toast here would interrupt whatever the reader
      // was doing to report a job they never started.
      if (!auto) toast.error(e.message || 'Could not refresh the check');
    } finally { setBusy(false); }
  };

  // Self-correct ONCE per mount, and only on evidence. `resolvedSinceSnapshot`
  // is the cheap probe's count of rows already tagged; `stale` is the 36h
  // fallback for the drift that probe cannot see (a location that became
  // UNtagged, or new GL postings — both owned by the import hook and the daily
  // timer). The ref is what keeps this from looping: reload() lands new data,
  // which re-runs this effect, and the recompute it triggered has by then made
  // the condition false anyway.
  useEffect(() => {
    if (autoFired.current || loading || error) return;
    if (!data?.available || data.status !== 'ready') return;
    if (!(data.resolvedSinceSnapshot > 0 || data.stale)) return;
    autoFired.current = true;
    recompute({ auto: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, loading, error]);

  const busy = refreshing || autoRefreshing;

  const RefreshBtn = ({ label = 'Refresh now' }) => (
    <Btn variant="ghost" size="sm" onClick={() => recompute()} loading={busy}>{label}</Btn>
  );

  const header = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
      <span style={{ display: 'flex', color: B.accent }}><Icon name="ledger" size={18} /></span>
      <span style={{ fontWeight: 700, fontSize: 15, color: B.text }}>Capital Deployment Verification</span>
      {!loading && !error && data?.available && data.status === 'ready' && (
        <span style={{ fontSize: 12, fontWeight: 600, color: B.textMd }}
          title={data.ignored?.count ? `${data.totals?.locationCount || 0} outstanding · ${data.ignored.count} ignored` : undefined}>
          {data.totals?.locationCount || 0}
          {data.ignored?.count > 0 && (
            <span style={{ fontWeight: 500, color: B.textLt }}> · {data.ignored.count} ignored</span>
          )}
        </span>
      )}
      <span style={{ flex: 1 }} />
      {/* SHOW IGNORED — a request parameter, not a client-side filter: the
          server applies the ignore before the per-section cap and before the
          totals. Offered only where migration 203 has landed, or the box would
          be a control over rows the server cannot mark. */}
      {!loading && !error && data?.available && data.status === 'ready' && data.ignoresAvailable && (
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: B.textMd, cursor: 'pointer', userSelect: 'none' }}
          title="Ignored codes are left off the list and out of the totals. Tick to see them, marked, with who ignored each and why.">
          <input type="checkbox" checked={showIgnored} onChange={e => toggleShowIgnored(e.target.checked)}
            style={{ margin: 0, accentColor: B.navy }} />
          Show ignored{data.ignored?.count > 0 ? ` (${data.ignored.count})` : ''}
        </label>
      )}
      {/* The "as of" stamp is load-bearing here: these numbers are a snapshot
          refreshed by a job, not a live read of the GL. While the widget is
          correcting itself it says so instead, naming the evidence it acted on
          — an unexplained spinner on a dashboard reads as something wrong. */}
      {!loading && !error && autoRefreshing && (
        <span style={{ fontSize: 11.5, color: B.textMd }}>
          {data?.resolvedSinceSnapshot > 0
            ? `${data.resolvedSinceSnapshot} since tagged — updating\u2026`
            : 'Updating\u2026'}
        </span>
      )}
      {!loading && !error && !autoRefreshing && data?.generatedUTC && (
        <span title={'These numbers are a snapshot, refreshed after each Sage GL import and daily, '
          + 'and automatically when this widget notices a location on the list has since been tagged.'
          + (data.durationMs ? ` Last recompute took ${(data.durationMs / 1000).toFixed(1)}s.` : '')}
          style={{ fontSize: 11.5, color: data.stale ? B.amber : B.textMd }}>
          as of {fmt.dateTime(data.generatedUTC)}
        </span>
      )}
      {!loading && data?.available && <RefreshBtn label="Refresh" />}
    </div>
  );

  const body = () => {
    if (loading) return <Spinner />;
    if (error)   return <ErrorMsg msg={error} onRetry={reload} />;

    if (!data?.available) {
      return (
        <Card>
          <EmptyState icon="info" title="Not available yet"
            message={data?.message || 'The Sage tables this check reads are not present on this database yet.'} />
        </Card>
      );
    }

    // The snapshot job has never produced a result yet (fresh deployment — the
    // first GL import or the daily timer will fill it in).
    if (data.status === 'never_run') {
      return (
        <Card>
          <EmptyState icon="info" title="Not computed yet"
            message="This check is refreshed after each Sage GL import and daily. Run it now to see the current position."
            action={<RefreshBtn />} />
        </Card>
      );
    }

    if (data.status === 'error') {
      return (
        <Card>
          <EmptyState icon="error" title="The last refresh failed"
            message={data.error || 'The snapshot could not be computed.'}
            action={<RefreshBtn label="Try again" />} />
        </Card>
      );
    }

    const locations = data.locations || [];
    const ignoredCount = data.ignored?.count || 0;
    if (locations.length === 0) {
      // Hidden ignored rows are not "accounted for" — they are a decision the
      // reader took, and the empty state says so rather than reporting a clean
      // book over a list that was emptied by hand.
      if (ignoredCount > 0 && !showIgnored) {
        return (
          <Card>
            <EmptyState icon="success" title="All remaining capital accounted for"
              message={`Every Sage location and entity carrying a balance is tagged to a deal, apart from the ${ignoredCount} ignored code${ignoredCount === 1 ? '' : 's'} hidden from this list. Tick "Show ignored" to see them.`}
              action={<Btn variant="ghost" size="sm" onClick={() => toggleShowIgnored(true)}>Show ignored</Btn>} />
          </Card>
        );
      }
      return (
        <Card>
          <EmptyState icon="success" title="All capital accounted for"
            message="Every Sage location and entity carrying a balance is tagged to a deal." />
        </Card>
      );
    }

    const t = data.totals || {};
    const byKind = Object.fromEntries(SECTIONS.map(sec => [sec.kind, locations.filter(l => l.kind === sec.kind)]));
    // A snapshot computed before migration 196 carries no kinds: every row then
    // reads as "location or not", and the entity section is not drawn at all
    // rather than drawn empty under a heading that claims to know.
    const kindsAvailable = t.kindsAvailable !== false;
    const sections = SECTIONS.filter(sec => byKind[sec.kind].length > 0 || (t.byKind?.[sec.kind] || 0) > 0)
      .filter(sec => kindsAvailable || sec.kind !== 'entity');
    // The preview cap is applied per section, so the first section cannot
    // consume it and hide the two beneath it.
    const previewPer = Math.max(1, Math.ceil(PREVIEW_ROWS / Math.max(1, sections.length)));
    const hiddenByPreview = sections.reduce((n, sec) =>
      n + Math.max(0, byKind[sec.kind].length - previewPer), 0);

    return (
      <Card style={{ padding: 0, overflow: 'hidden' }}>
        {/* Exposure summary — computed server-side over ALL offending
            codes, so it stays honest even when the list is capped. */}
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', padding: '14px 16px', borderBottom: `1px solid ${B.border}`, background: B.surfaceMuted }}>
          <Metric label="Locations" value={kindsAvailable ? (t.knownLocationCount || 0) : ((t.locationCount || 0) - (t.unknownLocationCount || 0))} color={B.amber}
            title="Sage locations with a non-zero balance and no deal-detail tag" />
          {kindsAvailable && (
            <Metric label="Entities" value={t.entityCount || 0} color={B.amber}
              title="Sage entities with a non-zero balance not attached to any deal" />
          )}
          {t.unknownLocationCount > 0 && (
            <Metric label="Not in master" value={t.unknownLocationCount} color={B.red}
              title="Codes posted in the GL that are on neither the Sage location master nor the entity master" />
          )}
          <Metric label="Net balance" value={fmt.currency(t.netBalance)}
            title="Sum of debits − credits across every untagged code" />
          <Metric label="Gross exposure" value={fmt.currency(t.absBalance)} color={B.navy}
            title="Sum of the absolute balances — debits and credits do not cancel out across codes" />
          {ignoredCount > 0 && (
            <Metric label="Ignored" value={`${ignoredCount} · ${fmt.currency(data.ignored.absBalance)}`} color={B.textLt}
              title={`${ignoredCount} code${ignoredCount === 1 ? '' : 's'} somebody has ignored, carrying ${fmt.currency(data.ignored.absBalance)} of gross exposure. Left out of every figure to the left${data.ignored.error ? ' — the ignored totals could not be read, so the figures to the left may still include them' : ''}.`} />
          )}
        </div>

        {sections.map(sec => {
          const all = byKind[sec.kind];
          const rows = showAll ? all : all.slice(0, previewPer);
          const total = t.byKind?.[sec.kind] ?? all.length;
          const truncated = !!data.truncatedByKind?.[sec.kind];
          return (
            <div key={sec.kind}>
              {/* Section heading: what this pile IS and how it is cleared. */}
              <div style={{ padding: '10px 16px 6px', borderBottom: `1px solid ${B.border}`, background: B.surface }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 700, fontSize: 13, color: sec.kind === 'unknown' ? B.red : B.text }}>{sec.title}</span>
                  <span style={{ fontSize: 12, fontWeight: 600, color: B.textMd }}>{total}</span>
                  {truncated && (
                    <span style={{ fontSize: 11, color: B.textMd }}>· showing the {all.length} largest</span>
                  )}
                </div>
                <div style={{ fontSize: 11.5, color: B.textMd, marginTop: 2 }}>{sec.hint}</div>
              </div>

              {/* Column header */}
              <div style={{ display: 'flex', alignItems: 'center', padding: '8px 16px', borderBottom: `2px solid ${B.border}`, background: B.surfaceMuted, fontSize: 11, fontWeight: 600, color: B.textMd, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                <div style={{ flex: 1, minWidth: 0 }}>{sec.column}</div>
                <div style={{ width: 110, textAlign: 'right' }}>Debits</div>
                <div style={{ width: 110, textAlign: 'right' }}>Credits</div>
                <div style={{ width: 120, textAlign: 'right' }}>Balance</div>
                <div style={{ width: 100, textAlign: 'right' }}>Last txn</div>
                {data.ignoresAvailable && <div style={{ width: 76 }} />}
              </div>

              {rows.length === 0 && (
                <div style={{ padding: '10px 16px', fontSize: 12, color: B.textMd, borderBottom: `1px solid ${B.divider}` }}>
                  None of the {total} stored — raise the read limit to see them.
                </div>
              )}

              {rows.map(loc => (
                <div key={loc.sageCode}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '10px 16px', borderBottom: `1px solid ${B.divider}`,
                    fontSize: 13,
                    // An ignored row (only ever on screen with the checkbox
                    // ticked) is muted rather than hidden, so it reads as a
                    // decision somebody took and not as an outstanding item.
                    opacity: loc.ignored ? 0.6 : 1,
                    flexWrap: 'wrap',
                  }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 600, color: B.text, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {loc.displayName || loc.sageCode}
                      </span>
                      {loc.displayName && (
                        <span style={{ fontSize: 11, color: B.textLt, fontFamily: 'monospace' }}>{loc.sageCode}</span>
                      )}
                      {loc.ignored && (
                        <span title={[
                            'Ignored — left off the default list and out of the totals.',
                            loc.ignoreReason ? `Reason: ${loc.ignoreReason}` : null,
                            loc.ignoredBy ? `By ${loc.ignoredBy}` : null,
                            loc.ignoredUTC ? `on ${fmt.dateTime(loc.ignoredUTC)}` : null,
                          ].filter(Boolean).join(' ')}
                          style={{ fontSize: 10, fontWeight: 700, color: B.textMd, background: B.surfaceMuted, border: `1px solid ${B.border}`, borderRadius: 10, padding: '1px 7px' }}>
                          Ignored{loc.ignoreReason ? ` · ${loc.ignoreReason}` : ''}
                        </span>
                      )}
                      {/* Keyed on kind, NOT on !isKnownLocation: an entity is
                          not a known LOCATION either, and this chip on it would
                          tell the reader to fix Sage about a code they can
                          attach in one click. */}
                      {loc.kind === 'unknown' && (
                        <span title="This code appears in the GL but has no row in the Sage location master or the entity master — import the Sage export, or correct the code in Sage."
                          style={{ fontSize: 10, fontWeight: 700, color: B.red, background: B.red + '18', border: `1px solid ${B.red}44`, borderRadius: 10, padding: '1px 7px' }}>
                          Not in master
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: B.textMd, marginTop: 2 }}>
                      {loc.parentName ? `${loc.parentName} · ` : ''}
                      {loc.transactionCount} transaction{loc.transactionCount === 1 ? '' : 's'}
                    </div>
                  </div>
                  <div style={{ width: 110, textAlign: 'right', color: B.textMd }}>{fmt.currency(loc.debits)}</div>
                  <div style={{ width: 110, textAlign: 'right', color: B.textMd }}>{fmt.currency(loc.credits)}</div>
                  <div style={{ width: 120, textAlign: 'right', fontWeight: 700, color: loc.balance < 0 ? B.red : B.text }}>
                    {fmt.currency(loc.balance)}
                  </div>
                  <div style={{ width: 100, textAlign: 'right', color: B.textMd, fontSize: 12 }}>
                    {fmt.date(loc.lastTransactionDate)}
                  </div>
                  {/* IGNORE / RESTORE — offered only where the server can
                      store the answer (migration 203). Ignore opens a one-line
                      reason box under the row; the reason is optional, but a
                      $2M code taken off a verification list with no note is
                      the kind of thing the next reader has to re-investigate. */}
                  {data.ignoresAvailable && (
                    <div style={{ width: 76, textAlign: 'right' }}>
                      {loc.ignored ? (
                        <Btn variant="ghost" size="sm" onClick={() => restoreCode(loc.sageCode)}
                          loading={ignoreBusy === loc.sageCode} style={{ padding: '4px 8px', fontSize: 11.5 }}>Restore</Btn>
                      ) : ignoring === loc.sageCode ? null : (
                        <Btn variant="ghost" size="sm" onClick={() => { setIgnoring(loc.sageCode); setIgnoreReason(''); }}
                          disabled={!!ignoreBusy} style={{ padding: '4px 8px', fontSize: 11.5 }}>Ignore</Btn>
                      )}
                    </div>
                  )}
                  {ignoring === loc.sageCode && !loc.ignored && (
                    <div style={{ flexBasis: '100%', display: 'flex', alignItems: 'center', gap: 8, paddingTop: 6 }}>
                      <input autoFocus value={ignoreReason} onChange={e => setIgnoreReason(e.target.value)}
                        placeholder={`Why ignore ${loc.sageCode}? (optional)`} maxLength={1000}
                        onKeyDown={e => { if (e.key === 'Enter') ignoreCode(loc.sageCode); if (e.key === 'Escape') setIgnoring(null); }}
                        style={{ flex: 1, fontSize: 12.5, padding: '6px 10px', border: `1px solid ${B.border}`, borderRadius: 8, color: B.text, background: B.surface }} />
                      <Btn variant="primary" size="sm" onClick={() => ignoreCode(loc.sageCode)} loading={ignoreBusy === loc.sageCode}>Ignore</Btn>
                      <Btn variant="ghost" size="sm" onClick={() => setIgnoring(null)} disabled={ignoreBusy === loc.sageCode}>Cancel</Btn>
                    </div>
                  )}
                </div>
              ))}
            </div>
          );
        })}

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', padding: '10px 16px' }}>
          <div style={{ fontSize: 11.5, color: B.textMd }}>
            Clearing a row from its section (tagging a location, attaching an entity) is spotted next time this page loads; Refresh does it now.
            {data.ignoresAvailable && ' Ignore takes a code off this list and out of the totals until it is restored.'}
            {data.truncated && ` Each section shows its largest ${data.limit} rows.`}
          </div>
          {hiddenByPreview > 0 || showAll ? (
            <Btn variant="ghost" size="sm" onClick={() => setShowAll(v => !v)}>
              {showAll ? 'Show fewer' : `Show all ${locations.length}`}
            </Btn>
          ) : null}
        </div>
      </Card>
    );
  };

  return <div>{header}{body()}</div>;
};

export default CapitalDeploymentWidget;
